
export interface FeatureFlags {
  enableProFeatures: boolean;
  enableCollaboration: boolean;
  enableAIAnalysis: boolean;
  enableLiveGuidance: boolean;
  enableRealtimeGuidance: boolean;
  enableSharing: boolean;
  debugMode: boolean;
}

const defaultFeatures: FeatureFlags = {
  enableProFeatures: true,
  enableCollaboration: true,
  enableAIAnalysis: true,
  enableLiveGuidance: false,
  enableRealtimeGuidance: false,
  enableSharing: true,
  debugMode: false,
};

// Feature flags controlled by PostHog
export const getFeatureFlags = (posthogFlags?: Record<string, boolean | string>): FeatureFlags => {
  // Start with defaults
  const flags = { ...defaultFeatures };
  
  // Apply PostHog feature flags
  if (posthogFlags) {
    // Map PostHog flag "live-guidance" to both live guidance features
    if ('live-guidance' in posthogFlags) {
      const liveGuidanceEnabled = Boolean(posthogFlags['live-guidance']);
      flags.enableLiveGuidance = liveGuidanceEnabled;
      flags.enableRealtimeGuidance = liveGuidanceEnabled;
    }
    
    // You can add more PostHog flags here in the future
    // if ('pro-features' in posthogFlags) {
    //   flags.enableProFeatures = Boolean(posthogFlags['pro-features']);
    // }
  }

  // Only non-PostHog flags can be overridden locally in development
  if (process.env.NODE_ENV === 'development') {
    const saved = localStorage.getItem('featureFlags');
    if (saved) {
      try {
        const localFlags = JSON.parse(saved);
        // Only apply local overrides for non-PostHog controlled flags
        const allowedLocalFlags = {
          enableProFeatures: localFlags.enableProFeatures,
          enableCollaboration: localFlags.enableCollaboration,
          enableAIAnalysis: localFlags.enableAIAnalysis,
          enableSharing: localFlags.enableSharing,
          debugMode: localFlags.debugMode,
          // Live guidance flags are NOT allowed to be overridden locally
        };
        Object.assign(flags, allowedLocalFlags);
      } catch (e) {
        console.warn('Invalid feature flags in localStorage, using defaults');
      }
    }
  }

  return flags;
};

export const setFeatureFlag = (flag: keyof FeatureFlags, value: boolean) => {
  // Prevent setting PostHog-controlled flags
  if (flag === 'enableLiveGuidance' || flag === 'enableRealtimeGuidance') {
    console.warn(`Feature flag "${flag}" is controlled by PostHog and cannot be set locally`);
    return;
  }

  const current = getFeatureFlags();
  const updated = { ...current, [flag]: value };
  localStorage.setItem('featureFlags', JSON.stringify(updated));
  window.dispatchEvent(new CustomEvent('featureFlagsChanged', { detail: updated }));
};
